<template>
  <div>
    <d-form>
      <d-form-row class="form-group">
        <d-col cols="12" md="8">
          <label>{{ $t('shapeLinks.form.name.label') }}</label>
          <d-form-input
            ref="nameInput"
            type="text"
            v-model="model.name"
            :state="hasErrorFiled('name')"
            :placeholder="$t('shapeLinks.form.name.placeholder')"
          />
          <d-form-invalid-feedback>
            {{ $t('shapeLinks.form.name.invalid') }}
          </d-form-invalid-feedback>
        </d-col>
        <d-col
          cols="12"
          md="4"
          class="d-flex align-items-end justify-content-around"
        >
          <d-checkbox
            toggle
            v-model="model.settings.showAnswer"
            class="custom-toggle-sm"
          >
            <span>{{ $t('shapeLinks.form.showAnswer.label') }}</span>
          </d-checkbox>
          <d-checkbox
            toggle
            v-model="model.settings.colored"
            class="custom-toggle-sm"
          >
            <span>{{ $t('shapeLinks.form.colored.label') }}</span>
          </d-checkbox>
        </d-col>
      </d-form-row>

      <hr class="my-4" />

      <d-form-row class="form-group align-items-end">
        <d-col cols="12" md="2">
          <label>{{ $t('shapeLinks.form.columns.label') }}</label>
          <d-form-input
            type="number"
            min="4"
            v-model.number="model.columns"
            @input="updateTable"
          />
        </d-col>
        <d-col cols="12" md="2">
          <label>{{ $t('shapeLinks.form.rows.label') }}</label>
          <d-form-input
            type="number"
            min="4"
            v-model.number="model.rows"
            @input="updateTable"
          />
        </d-col>

        <d-col cols="12" md="4">
          <div class="d-flex justify-content-between align-items-center mb-1">
            <label class="mb-0">
              {{ $t('shapeLinks.form.numPairs.label') }}
              <i
                id="tooltip-pairs-style"
                class="fas fa-info-circle text-muted ml-1"
                style="cursor: pointer"
              />
              <d-tooltip target="tooltip-pairs-style" placement="top">
                {{ autoPairsMessage }}
              </d-tooltip>
            </label>

            <d-checkbox
              toggle
              v-model="model.settings.autoPairs"
              class="custom-toggle-sm"
            >
              <small
                :class="
                  model.settings.autoPairs ? 'text-primary' : 'text-muted'
                "
              >
                {{ $t('shapeLinks.form.numPairs.auto') }}
              </small>
            </d-checkbox>
          </div>

          <d-form-input
            type="number"
            :disabled="model.settings.autoPairs"
            v-model.number="model.settings.num_pairs"
          />
        </d-col>

        <d-col cols="12" md="4" v-if="model.settings.numberStyle !== 'plain'">
          <label class="d-flex align-items-center">
            {{ $t('shapeLinks.form.filled.label') }}
            <i
              id="tooltip-fill-style"
              class="fas fa-info-circle text-muted ml-1"
              style="cursor: pointer"
            />
            <d-tooltip target="tooltip-fill-style" placement="top">
              {{ fillStyleMessage }}
            </d-tooltip>
          </label>
          <d-select
            v-model="model.settings.filled"
            class="form-control"
            :disabled="showAutoWarning"
          >
            <option value="auto">
              {{ $t('shapeLinks.form.filled.auto') }}
            </option>
            <option value="filled">
              {{ $t('shapeLinks.form.filled.filled') }}
            </option>
            <option value="outline">
              {{ $t('shapeLinks.form.filled.outline') }}
            </option>
          </d-select>
        </d-col>
      </d-form-row>

      <d-form-row class="form-group">
        <d-col cols="12" md="2">
          <label>{{ $t('shapeLinks.form.shapeMode.label') }}</label>
          <d-select v-model="model.settings.shapeMode" class="form-control">
            <option value="geometric">
              {{ $t('shapeLinks.form.shapeMode.geometric') }}
            </option>
            <option value="number">
              {{ $t('shapeLinks.form.shapeMode.number') }}
            </option>
          </d-select>
        </d-col>

        <d-col cols="12" md="2">
          <label>{{ $t('shapeLinks.form.pathWeight.label') }}</label>
          <d-select v-model="model.settings.pathWeight" class="form-control">
            <option value="thin">
              {{ $t('shapeLinks.form.pathWeight.thin') }}
            </option>
            <option value="normal">
              {{ $t('shapeLinks.form.pathWeight.normal') }}
            </option>
            <option value="bold">
              {{ $t('shapeLinks.form.pathWeight.bold') }}
            </option>
          </d-select>
        </d-col>

        <d-col cols="12" md="2" v-if="model.settings.shapeMode === 'number'">
          <label>{{ $t('shapeLinks.form.numberStyle.label') }}</label>
          <d-select v-model="model.settings.numberStyle" class="form-control">
            <option value="plain">
              {{ $t('shapeLinks.form.numberStyle.plain') }}
            </option>
            <option value="circle">
              {{ $t('shapeLinks.form.numberStyle.circle') }}
            </option>
            <option value="square">
              {{ $t('shapeLinks.form.numberStyle.square') }}
            </option>
            <option value="rounded">
              {{ $t('shapeLinks.form.numberStyle.rounded') }}
            </option>
          </d-select>
        </d-col>
      </d-form-row>
    </d-form>

    <div class="position-relative">
      <board-grid
        v-if="model.table.length"
        :table="model.table"
        :rows="model.rows"
        :columns="model.columns"
        :cell-size="cellSize"
        :settings="model.settings"
      />
    </div>
  </div>
</template>

<script>
// eslint-disable-next-line import/no-webpack-loader-syntax
import ShapeWorker from 'worker-loader!_utils/shapeLinks/worker.js';
import { mapActions } from 'vuex';

import mixin from '_common/mixins/ValidateForm';
import { cloneDeep } from '_utils';
import Events from '_utils/events';
import PuzzleService from '_services/rest/modules/puzzle';
import FormatErrors from '_common/mixins/FormatErrors';
import workerManager from '_common/mixins/workerManager';
import BoardGrid from '_common/components/board/BoardGrid.vue';
import { _LOAD_SHAPE_MODEL_, CELL, CELL_SIZE, MODEL } from './ShapeModel';

export default {
  name: 'ShapeForm',
  components: { BoardGrid },
  mixins: [mixin, FormatErrors, workerManager],
  data() {
    return {
      model: cloneDeep(MODEL),
      cellSize: CELL_SIZE,
      errors: { name: { required: false } },
      part1Queue: [],
      idleWorkers: [],
      isDrawing: false,
      pathStack: [],
      activeColor: null,
      currentSegment: null,
    };
  },
  computed: {
    autoPairsMessage() {
      if (this.model.settings.autoPairs) {
        return this.$t('shapeLinks.form.numPairs.tooltipAuto', {
          count: this.model.settings.num_pairs,
        });
      }
      return this.$t('shapeLinks.form.numPairs.tooltipManual');
    },
    fillStyleMessage() {
      if (this.showAutoWarning) {
        return this.$t('shapeLinks.form.filled.tooltipWarning');
      }
      return this.$t('shapeLinks.form.filled.tooltipInfo');
    },
    showAutoWarning() {
      return this.model.settings.num_pairs > 9;
    },
    isCollaborativeMode() {
      return this.model.columns >= 9;
    },
  },
  methods: {
    ...mapActions([
      'setToggle',
      'activeAlert',
      'setShapeLinks',
      'setHasChanges',
    ]),
    resolveWorkerErrorMessage(data) {
      if (data.errorCode) {
        return this.$t(`shapeLinks.alert.workerErrors.${data.errorCode}`);
      }

      return data.message || this.$t('shapeLinks.alert.generateError');
    },
    updateTable() {
      const { rows, columns } = this.model;
      const newTable = [];
      for (let y = 0; y < rows; y += 1) {
        for (let x = 0; x < columns; x += 1) {
          const existing = this.model.table.find(
            c => Number(c.positionx) === x && Number(c.positiony) === y,
          );
          newTable.push(
            existing || {
              ...CELL,
              id: y * columns + x,
              positionx: x,
              positiony: y,
            },
          );
        }
      }
      this.model.table = newTable;
    },
    generate() {
      this.prepareGeneration();

      const numPairs = this.resolveNumPairs();
      const workerCount = this.resolveWorkerCount();
      const state = { finished: false, errorCount: 0 };

      for (let i = 0; i < workerCount; i += 1) {
        const worker = this.createWorker(i, workerCount, state);
        this.assignWorkerRole(worker, i, numPairs);
      }
    },
    prepareGeneration() {
      this.cancelPreviousGeneration();
      this.startGenerationLoader();
      this.part1Queue = [];
      this.idleWorkers = [];
      this.workers = [];
    },
    createWorker(index, total, state) {
      const worker = new ShapeWorker();
      worker.onmessage = this.makeMessageHandler(worker, total, state);
      worker.onerror = this.makeErrorHandler(worker, total, state, index);
      this.workers.push(worker);
      return worker;
    },
    assignWorkerRole(worker, index, numPairs) {
      const payload = this.buildWorkerPayload(numPairs, index);

      if (this.isCollaborativeMode) {
        return this.setupCollaborativeWorker(worker, index, payload);
      }

      return this.setupCompetitorWorker(worker, payload);
    },
    setupCollaborativeWorker(worker, index, payload) {
      if (index === 0) {
        worker.postMessage({ ...payload, role: 'producer', variations: 10 });
      } else {
        this.idleWorkers.push(worker);
      }
    },
    setupCompetitorWorker(worker, payload) {
      worker.postMessage({ ...payload, role: 'competitor' });
    },
    assignTasks() {
      while (this.part1Queue.length > 0 && this.idleWorkers.length > 0) {
        const worker = this.idleWorkers.shift();
        const part1State = this.part1Queue.shift();
        const workerId = this.workers.indexOf(worker);

        worker.postMessage({
          ...this.buildWorkerPayload(this.resolveNumPairs(), workerId),
          role: 'consumer',
          initialState: part1State,
        });
      }
    },
    success({ data, state }) {
      state.finished = true;
      this.model.table = data.table;
      this.cleanupWorkers();
      this.activeAlert({
        message: this.$t('shapeLinks.alert.generated'),
        theme: 'success',
      });
    },
    producedParts({ data, worker }) {
      this.part1Queue.push(...data.states);
      this.idleWorkers.push(worker);
      this.assignTasks();
    },
    consumerFail({ worker, workerCount, state }) {
      this.idleWorkers.push(worker);

      if (
        this.part1Queue.length === 0 &&
        this.idleWorkers.length === workerCount
      ) {
        state.finished = true;
        this.cleanupWorkers();
        this.activeAlert({
          message: this.$t('shapeLinks.alert.generateFailed'),
          theme: 'danger',
        });
      } else if (this.part1Queue.length > 0) {
        this.assignTasks();
      }
    },
    error({
      data, worker, state, workerCount,
    }) {
      state.errorCount += 1;
      worker.terminate();
      if (state.errorCount === workerCount && !state.finished) {
        state.finished = true;
        this.cleanupWorkers();
        this.activeAlert({
          message: this.resolveWorkerErrorMessage(data),
          theme: 'danger',
        });
      }
    },
    handleWorkerMessage(status, workerCtx) {
      if (status === 'success') this.success(workerCtx);
      if (status === 'produced_parts') this.producedParts(workerCtx);
      if (status === 'consumer_fail') this.consumerFail(workerCtx);
      if (status === 'error') this.error(workerCtx);
    },
    makeMessageHandler(worker, workerCount, state) {
      return ({ data }) => {
        if (state.finished) return;
        this.handleWorkerMessage(data.status, {
          data,
          worker,
          workerCount,
          state,
        });
      };
    },
    makeErrorHandler(worker, workerCount, state, workerIndex) {
      return err => {
        console.error(`ShapeWorker ${workerIndex + 1} error:`, err);
        state.errorCount += 1;
        worker.terminate();
        if (state.errorCount === workerCount && !state.finished) {
          state.finished = true;
          this.$store.dispatch('hideLoader');
          this.activeAlert({
            message: this.$t('shapeLinks.alert.workerError'),
            theme: 'danger',
          });
        }
      };
    },
    resolveWorkerCount() {
      return navigator.hardwareConcurrency
        ? Math.min(navigator.hardwareConcurrency, 4)
        : 2;
    },
    buildWorkerPayload(numPairs, workerId) {
      return {
        workerId,
        model: { rows: this.model.rows, columns: this.model.columns },
        numPairs,
        options: {
          algorithm: this.model.settings?.algorithm,
          batchSize: 10,
          filled: this.model.settings.filled,
        },
      };
    },
    resolveNumPairs() {
      let numberPairs = parseInt(this.model.settings.num_pairs, 10) || 2;

      if (this.model.settings.autoPairs) {
        const area = this.model.rows * this.model.columns;
        const ideal = this.calculateIdealPairs(area);
        this.model.settings.num_pairs = ideal;
        numberPairs = ideal;
      }

      this.onNumPairsChange(numberPairs);

      return numberPairs;
    },
    calculateIdealPairs(area) {
      const ranges = [
        { max: 36, divisor: 5 },
        { max: 64, divisor: 7 },
        { max: 100, divisor: 9 },
        { max: 144, divisor: 10 },
        { max: Infinity, divisor: 12 },
      ];
      const range = ranges.find(r => area <= r.max);
      const result = Math.floor(area / range.divisor);
      return Math.max(2, result);
    },
    onNumPairsChange(val) {
      if (val >= 9) {
        this.model.settings.filled = 'auto';
      }
    },
    onAutoPairsChange(isAuto) {
      if (isAuto) {
        this.resolveNumPairs();
      }
    },
    save() {
      if (this.checkForm()) {
        const puzzle = {
          ...this.model,
          slug: `${this.model.name.replace(/\s+/g, '-')}-shape-links`,
        };
        PuzzleService.save(puzzle)
          .then(response => {
            this.activeAlert({
              message: this.$t('shapeLinks.alert.gameSaved'),
              theme: 'success',
            });
            this.setShapeLinks(response);
            if (this.$route.name === 'admin-shape-links-new') {
              this.$router.push({
                name: 'admin-shape-links-edit',
                params: { puzzleId: response.id },
              });
            }
            this.setHasChanges(false);
          })
          .catch(error => {
            const errors = this.formatErrors(error.data);
            this.activeAlert({ message: errors, theme: 'danger' });
          });
      }
    },
  },
  watch: {
    'model.num_pairs': 'onNumPairsChange',
    'model.settings.autoPairs': 'onAutoPairsChange',

    'model.rows': 'resolveNumPairs',
    'model.columns': 'resolveNumPairs',
  },
  destroyed() {
    Events.$off(_LOAD_SHAPE_MODEL_);
  },
  mounted() {
    this.resolveNumPairs();
    Events.$on(_LOAD_SHAPE_MODEL_, model => {
      const settings = model.settings || this.model.settings;
      this.model = { ...model, settings };
    });
  },
};
</script>
